import React, { useState, useEffect } from "react"
import { Modal, Button } from "react-bootstrap"
import deepEqual from "deep-equal"

import { withRunEventLabelState, isLTPM } from "../../../../../components/shared_components_registration"
import { withPlotSettingsConfig } from "../../../../config/PlotSettingsConfigProvider"
import { isNumber, stringifyDate } from "../../../../utils"
import { getObjectsList, addNewObject, updateObject } from "../../../../../Loaders"
import { logToServer } from "../../../../../ErrorBoundary"
import { useGraphImageState } from "../../../modals/GraphImageModal"
import { Required } from "../../../../widgets/utils"
import ToggleEventLayers, { LabelDetails } from "./ToggleEventLayers"
import InfoPopover from "../../../../widgets/InfoPopover"
import UpdateSelectionRange from "./UpdateSelectionRange"
import ErrMessage from "./ErrMessage"
import ReactSelectionElement from "../../../../widgets/ReactSelectionElement"

export const ChangeMessage = ({
  isEditing = false,
  isDate = false,
  current = "",
  original = "",
  label = "",
}) => {
  const displayValue = isDate ? stringifyDate(original) : original
  return (
    <>
      {isEditing && original !== current && (
        <div style={{ color: "var(--lanzalake-orange)" }}>{`Previous ${label}: ${
          displayValue || "N/A"
        }`}</div>
      )}
    </>
  )
}

function CreateRunEventLabelModal({
  show,
  onHide,
  run = {},
  plotImageSettings = {},
  PlotImage = <></>,
  exportBaseName,
  validRunEventOpts = [],

  // From PlotSettingsConfigProvider
  timeExtractor,

  // From RunLabelConfigProvider
  newLabelSelection,
  setNewLabelSelection,
  addingRunEventLabel,
  setAddingRunEventLabel,
  toggleRunEventVisibility,
}) {
  if (isLTPM) {
    return <></>
  }
  const otherString = "Other"
  const [defaultLabelDetails, setDefaultLabelDetails] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [selectionChanged, setSelectionChanged] = useState(false)

  const { baseTime, minTime, maxTime, single, offset, maxFullTime } = plotImageSettings
  const [x, dim, _, __] = useGraphImageState(
    null,
    timeExtractor,
    baseTime,
    minTime,
    maxTime,
    single,
    offset,
    maxFullTime,
    800,
  )

  const [eventType, setEventType] = useState({})
  const [showEventDetails, setShowEventDetails] = useState(false)
  const [selectedOption, setSelectedOption] = useState("")
  const [customOption, setCustomOption] = useState("")
  const [optionConfirmed, setOptionConfirmed] = useState(false)
  const [comment, setComment] = useState("")

  const initializeForm = (labelDetails) => {
    const event = validRunEventOpts.find((e) => e.name === labelDetails.event_type)
    if (event) {
      setEventType(event)
      setSelectedOption(labelDetails.selected_option || "")
      setCustomOption(labelDetails.custom_option || "")
      setComment(labelDetails.comment || "")
      setOptionConfirmed(labelDetails.option_confirmed)
    }
    setSelectionChanged(false)
  }

  useEffect(() => {
    if (newLabelSelection?.editing_id) {
      setDefaultLabelDetails(newLabelSelection)
      setIsEditing(true)
      initializeForm(newLabelSelection)
    }
  }, [])

  useEffect(() => {
    if (isEditing && !deepEqual(defaultLabelDetails, newLabelSelection)) {
      setSelectionChanged(true)
    }
  }, [newLabelSelection])

  const [eventOptions, setEventOptions] = useState([])
  const [eventOptionsLabel, setEventOptionsLabel] = useState("")
  const [optionsErrMsg, setOptionsErrMsg] = useState("")
  const [labelErrMsg, setLabelErrMsg] = useState("")
  useEffect(() => {
    if (eventType?.id && eventType?.has_options) {
      const { site, reactor } = run
      getObjectsList(
        `/warehouse/runlabels/event_options/?event_type_id=${eventType.id}&options_filter=${site}`,
      )
        .then((options) => {
          setEventOptionsLabel(options["label"])
          setEventOptions((options["list"] || []).filter((r) => r !== reactor))
        })
        .catch((err) => {
          logToServer(err)
          console.error(err)
          setOptionsErrMsg("There was a problem getting event options")
        })
    }
  }, [eventType])

  const updateEventType = (re) => {
    if (re?.name !== eventType?.name) {
      setEventType(re)
      setEventOptionsLabel("")
      setEventOptions([])
      setSelectedOption("")
      setCustomOption("")
      setOptionConfirmed(true)
      setComment("")
      setOptionsErrMsg("")
      setSelectionChanged(true)
    }
  }

  const showOptionConfirmedInput = selectedOption && (selectedOption !== otherString || customOption)
  const optionConfirmLabel = ` [${optionConfirmed ? "Confirmed" : "Suspected"}]`
  const optionLabel = `${eventOptionsLabel || "Selection"}${showOptionConfirmedInput && optionConfirmLabel}`

  return (
    <Modal show={show} onHide={onHide} style={{ overflowX: "scroll" }} enforceFocus={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>{`${
          isEditing ? "Editing" : "Creating New"
        } Run Event Label: ${exportBaseName}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <label>Label Details:</label>&nbsp;
          {isEditing && selectionChanged && (
            <Button
              variant="outline-primary"
              onClick={() => {
                setNewLabelSelection(defaultLabelDetails)
                initializeForm(defaultLabelDetails)
              }}
              className={"btn-sm"}
            >
              Reset Label Details
            </Button>
          )}
        </div>
        <div
          style={{
            padding: "10px 20px",
            borderRadius: "5px",
          }}
          className="solid-grey-border"
        >
          <UpdateSelectionRange
            selection={newLabelSelection}
            updateSelection={setNewLabelSelection}
            timeExtractor={timeExtractor}
            run={run}
            isSelecting={addingRunEventLabel}
            setIsSelecting={setAddingRunEventLabel}
            isEditing={isEditing}
            defaultDetails={defaultLabelDetails}
          />
          <hr />
          <div className={"row"} style={{ marginBottom: "15px" }}>
            <div className={"col-4"}>
              {validRunEventOpts.length > 0 ? (
                <>
                  <label>Event Type:</label>
                  &nbsp;
                  <ToggleEventLayers
                    small={true}
                    canToggleVisibility={false}
                    site={run.site}
                    reactor={run.reactor}
                    selectEventLabel={(event) => updateEventType(event)}
                  />
                  <ChangeMessage
                    isEditing={isEditing}
                    current={eventType?.name}
                    original={defaultLabelDetails?.["event_type"]}
                    label={"value"}
                  />
                  <div style={{ display: "flex", width: "100%" }}>
                    <ReactSelectionElement
                      name={"eventType"}
                      klass={"width-100"}
                      list={validRunEventOpts.map((label) => ({
                        value: label,
                        label: label.name,
                      }))}
                      getOptionLabel={(obj) => obj.label}
                      value={{
                        value: eventType || {},
                        label: eventType?.name || "",
                      }}
                      onValueChange={(_, selected) => updateEventType(selected?.value)}
                      isMulti={false}
                      isClearable={false}
                    />
                    &nbsp;
                    <Required />
                  </div>
                  {eventType?.name && (
                    <div
                      style={{
                        backgroundColor: "var(--lanzalake-grey-light-2)",
                        fontSize: "90%",
                        borderRadius: "5px",
                      }}
                      className="solid-grey-border"
                    >
                      <div
                        onClick={() => setShowEventDetails(!showEventDetails)}
                        style={{
                          cursor: "pointer",
                          padding: "5px",
                          color: "var(--lanzalake-white)",
                          backgroundColor: "var(--lanzalake-blue-primary)",
                        }}
                      >
                        Show Event Details &nbsp;
                        <span
                          className={`fa fa-caret-${showEventDetails ? "up" : "down"}`}
                          style={{ color: "var(--lanzalake-white)" }}
                        />
                      </div>
                      {showEventDetails && (
                        <LabelDetails eventType={eventType} divStyle={{ padding: "10px" }} />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div>{`No Label Options Available for ${run.site}/${run.reactor}`}</div>
              )}
            </div>
            <div className={"col-4 solid-grey-border"} style={{ borderRadius: "5px", padding: "10px" }}>
              <ErrMessage isErr={optionsErrMsg} errMsg={optionsErrMsg} />
              {eventOptions.length > 0 && (
                <>
                  <label>{optionLabel}:</label>
                  <ChangeMessage
                    isEditing={isEditing}
                    current={selectedOption}
                    original={defaultLabelDetails?.["selected_option"]}
                    label={"selection"}
                  />
                  <div style={{ display: "flex" }}>
                    <ReactSelectionElement
                      name={"eventOption"}
                      klass={"width-100"}
                      placeholder={eventType?.is_cross_reactor_event ? "Select Reactor" : "Select Option"}
                      list={eventOptions.map((opt) => ({
                        value: opt,
                      }))}
                      value={{
                        value: selectedOption || "",
                      }}
                      onValueChange={(_, opt) => {
                        const r = opt?.value || ""
                        if (selectedOption !== r) {
                          setSelectedOption(r)
                          setCustomOption("")
                          setOptionConfirmed(true)
                          setSelectionChanged(true)
                        }
                      }}
                      isMulti={false}
                    />
                    &nbsp;
                    <Required />
                  </div>
                  {selectedOption === otherString && (
                    <div style={{ marginTop: "5px" }}>
                      <label>Custom {optionLabel}:</label>
                      {defaultLabelDetails.selected_option === otherString && (
                        <ChangeMessage
                          isEditing={isEditing}
                          current={customOption}
                          original={defaultLabelDetails?.["custom_option"]}
                          label={"custom option"}
                        />
                      )}
                      <div>
                        <input
                          type={"text"}
                          style={{ width: "95%" }}
                          value={customOption || ""}
                          onChange={(e) => {
                            setCustomOption(e.target.value)
                            setOptionConfirmed(true)
                            setSelectionChanged(true)
                          }}
                        />
                        &nbsp;
                        <Required />
                      </div>
                    </div>
                  )}
                  {showOptionConfirmedInput && (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div className={"form-check form-switch"} style={{ marginTop: "10px" }}>
                        <input
                          type={"checkbox"}
                          id={"option-confirm-toggle"}
                          className={"form-check-input"}
                          checked={optionConfirmed}
                          onChange={() => {
                            setOptionConfirmed(!optionConfirmed)
                            setSelectionChanged(true)
                          }}
                        />
                        <label htmlFor={"option-confirm-toggle"} className={"form-check-label"}>
                          Confirm "{eventOptionsLabel || "Selection"}" Details
                        </label>
                      </div>
                      &nbsp;
                      <InfoPopover
                        desc={
                          "Toggle this if you have confirmed your selection. Otherwise the selection is " +
                          "considered 'suspected' rather than 'confirmed'."
                        }
                        placement={"right"}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={"col-4"}>
              <label>Additional Label Comment (Optional):</label>
              <ChangeMessage
                isEditing={isEditing}
                current={comment}
                original={defaultLabelDetails?.["comment"]}
                label={"comment"}
              />
              <div>
                <textarea
                  className={"form-control form-control-sm"}
                  onChange={(e) => {
                    setComment(e.target.value)
                    setSelectionChanged(true)
                  }}
                  value={comment || ""}
                  rows={"2"}
                />
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className={"row"}>{PlotImage(dim.width, dim.height, x)}</div>
      </Modal.Body>
      <Modal.Footer>
        <ErrMessage isErr={labelErrMsg} errMsg={labelErrMsg} />
        <ErrMessage
          isErr={newLabelSelection?.max < newLabelSelection?.min}
          errMsg={"`max` must be larger than `min`"}
        />
        <Button variant="outline-primary" onClick={onHide}>
          Close
        </Button>
        <Button
          disabled={
            !newLabelSelection?.runID ||
            (!newLabelSelection?.min && !isNumber(newLabelSelection.min)) || // To allow for min to be 0
            !newLabelSelection?.max ||
            newLabelSelection?.max < newLabelSelection?.min ||
            validRunEventOpts.length === 0 ||
            !eventType?.name ||
            (eventType?.has_options && !selectedOption) ||
            (selectedOption === otherString && !customOption) ||
            newLabelSelection?.placeholder_end_date ||
            !selectionChanged
          }
          variant="primary"
          onClick={() => {
            const data = {
              event_type_id: eventType.id,
              start: newLabelSelection.min,
              end: newLabelSelection.max,
              option: selectedOption,
              custom_option: customOption,
              option_confirmed: optionConfirmed,
              timeExtractor: timeExtractor.name,
              comment: comment,
            }
            const submitMethod = isEditing ? updateObject : addNewObject
            submitMethod(
              `/warehouse/runlabels/${newLabelSelection.runID}/${
                isEditing ? "update" : "create"
              }_runreactor_label/`,
              isEditing
                ? {
                    run_label_id: defaultLabelDetails.editing_id,
                    ...data,
                  }
                : data,
            )
              .then((_) => {
                onHide()
                run.reloadRunEventLabels && run.reloadRunEventLabels()
                setLabelErrMsg("")
              })
              .catch((err) => {
                console.error(err)
                logToServer(err)
                setLabelErrMsg(`There was a problem ${isEditing ? "updating" : "creating"} run event label.`)
              })
            if (!eventType.show) {
              toggleRunEventVisibility(eventType.id)
            }
          }}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withRunEventLabelState(withPlotSettingsConfig(CreateRunEventLabelModal))
